import { AdornisRouter } from '@adornis/base/client/a-router';
import { Maybe } from '@adornis/base/utilTypes';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-button';
import '@adornis/chemistry/elements/components/x-flex';
import { ElementsLink } from '@adornis/chemistry/elements/components/x-link';
import { ElementsDialog } from '@adornis/dialog/x-dialog';
import '@adornis/germanium/components/ge-button';
import GermaniumDialog from '@adornis/germanium/components/ge-dialog';
import '@adornis/germanium/components/ge-input';
import { GermaniumInput } from '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-link';
import '@adornis/germanium/components/ge-section';
import '@adornis/germanium/fonts';
import '@adornis/ghost/client/ghost-page-content';
import { ADropTrigger } from '@adornis/popover/a-drop-wrapper';
import { config } from 'config';
import { NewsLetterMail } from 'db/news-letter-mail';
import { WTGUser } from 'db/wtg-user';
import { html, nothing } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, state } from 'lit/decorators.js';
import { Observable, startWith } from 'rxjs';
import { MenuItem } from './wtg-navbar';

@customElement('wtg-footer')
export class WTGFooter extends ChemistryLitElement {
  size = new SizeController(this);

  @state() user = new ObservableController(
    this,
    WTGUser.currentUser.pipe(startWith(undefined)) as Observable<Maybe<WTGUser>>,
  );

  @state() page = new ObservableController(this, AdornisRouter.get('page'));

  render() {
    return html`
      <x-flex ${css({ background: this.colors.secondary, width: '100%', color: this.colors.on.secondary })}>
        <ge-section>
          <footer ${css({ width: '100%', padding: `${this.spacing.xl} 0` })}>
            <x-flex space="xl">
              <x-flex id="contact" horizontal wrap space="lg"> ${this.links()} ${this.moreContent()} </x-flex>

              <x-flex horizontal center space="lg">
                <ge-link href="https://www.internationalfoundersaward.com/impressum">
                  <ge-mini ${css({ textDecoration: 'underline' })}> Imprint </ge-mini>
                </ge-link>
                <ge-link href="https://www.internationalfoundersaward.com/datenschutz">
                  <ge-mini
                    ${css({ textDecoration: 'underline' })}
                    @click=${() =>
                      ElementsDialog.alert(html` <ghost-page-content page-slug="privacy"></ghost-page-content> `, 'OK')}
                  >
                    Privacy
                  </ge-mini>
                </ge-link>
                <ge-mini> @ 2022 - Welcome to Germany </ge-mini>
              </x-flex>
            </x-flex>
          </footer>
        </ge-section>
      </x-flex>
    `;
  }

  private menuItems: MenuItem[] = [
    { name: 'about us', link: '/' },
    { name: 'blog', link: '/blog' },
    {
      name: 'community',
      menuItems: [
        { name: 'forum', link: config.get('NODEBB_DOMAIN') },
        { name: 'events', link: '/events' },
        { name: 'groups', link: config.get('NODEBB_DOMAIN') },
        // { name: 'mentorship', link: '/404' },
      ],
    },
    { name: 'welcome box', link: '/welcome_box', guard: () => !!this.user.value },
    // { name: 'partner services', link: '/404' },
    {
      name: 'more',
      menuItems: [
        { name: 'talks', link: 'https://youtube.com/@welcometogermany4660' },
        { name: 'for entrepreneurs', link: 'https://www.internationalfoundersaward.com/' },
        { name: 'FAQs', link: '/faq' },
        { name: 'contact', link: '/404' },
      ],
    },
  ];
  private links() {
    const renderMenuItem = (menuItem: MenuItem, level: number = 0) => html`
      <x-link
        slot="trigger"
        href=${ifDefined(menuItem.link)}
        target=${menuItem.link === config.get('NODEBB_DOMAIN') ? '_self' : ''}
        @click=${(e: Event) => (e.currentTarget as ElementsLink).querySelector<ADropTrigger>('a-dropdown')?.toggle()}
        ${css({ padding: this.spacing.md, display: 'flex', alignItems: 'center' })}
      >
        <a-dropdown>
          <x-flex horizontal space="xs" crossaxis-center>
            <ge-text ?active=${this.page.value === menuItem.link?.split('/')[1]}>
              ${menuItem.name.toUpperCase()}
            </ge-text>

            ${menuItem.name === 'more' ? html` <x-icon ${css({ fontSize: '18px' })}> expand_more </x-icon> ` : nothing}
          </x-flex>
          <ge-card
            ${css({ padding: '0' })}
            slot="dropdown-content"
            @click=${(e: Event) =>
              this.shadowRoot?.querySelectorAll<ADropTrigger>('a-dropdown').forEach(drop => drop.close())}
          >
            <x-flex> ${menuItem.menuItems?.map(item => renderMenuItem(item, level + 1))} </x-flex>
          </ge-card>
        </a-dropdown>
      </x-link>
    `;

    return html`
      <x-flex flex crossaxis-start>
        ${this.menuItems.map(menuItem => (menuItem.guard?.() || true ? renderMenuItem(menuItem) : nothing))}
      </x-flex>
    `;
  }

  private moreContent() {
    return html`
      <x-flex flex space="xl">
        <x-flex space="md">
          <ge-h4> Subscribe for Updates! </ge-h4>
          <x-flex horizontal crossaxis-end space="md" ${css({ width: this.size.isTablet ? '100%' : '80%' })}>
            <x-flex space="sm">
              <ge-text> Email </ge-text>
              <ge-input
                id="newsletterinput"
                on-dark
                placeholder="user@example.de"
                .postfix=${html`
                  <x-icon
                    type="material-outlined"
                    ${css({ width: '20px', height: '20px', fontFamily: 'Material Icons Outlined' })}
                  >
                    email
                  </x-icon>
                `}
              ></ge-input>
            </x-flex>

            <x-flex ${css({ maxHeight: '40px' })}>
              <ge-button
                @click=${async () => {
                  const input = this.shadowRoot?.querySelector('#newsletterinput') as GermaniumInput;

                  if (!input.value.value) return;
                  if (!this.validateEmail(input.value.value as string))
                    return GermaniumDialog.showPopup({
                      content: close => html`
                        <x-flex space="md" padding="md" crossaxis-center center>
                          <ge-h4>Please provide a valid email</ge-h4>
                          <ge-button @click=${close}>Ok</ge-button>
                        </x-flex>
                      `,
                    });
                  const res = await NewsLetterMail.addEmailToNewsLetter(input.value.value as string)(
                    NewsLetterMail.allFields,
                  );

                  if (!res)
                    return GermaniumDialog.showPopup({
                      content: close => html`
                        <x-flex space="md" padding="md" crossaxis-center center>
                          <ge-h4> Email already in our NewsLetter </ge-h4>
                          <ge-button @click=${close}>Ok</ge-button>
                        </x-flex>
                      `,
                    });
                  return GermaniumDialog.showPopup({
                    content: close => html`
                      <x-flex space="md" padding="md" crossaxis-center center>
                        <ge-h4> Email was added to out NewsFeed </ge-h4>
                        <ge-button @click=${close}> Ok </ge-button>
                      </x-flex>
                    `,
                  });
                }}
              >
                SUBSCRIBE
              </ge-button>
            </x-flex>
          </x-flex>
        </x-flex>

        <x-flex space="md">
          <ge-h4> Follow us </ge-h4>

          <x-flex horizontal space="md">
            <x-link href="https://www.instagram.com/welcometogermany.info/">
              <x-image
                mode="width"
                ${css({ width: '27px' })}
                .value=${require('client/assets/social_media_icons/instagram.svg')}
              ></x-image>
            </x-link>
            <x-link href="https://www.youtube.com/@welcometogermany4660">
              <x-image
                mode="width"
                ${css({ width: '27px' })}
                .value=${require('client/assets/social_media_icons/youtube.svg')}
              ></x-image>
            </x-link>
            <x-link
              href="https://www.facebook.com/Whinker/?paipv=0&eav=AfZWvkOHSqeIoWa8hg-tKzblyHZmDMWREExUTszIOrBUHYoDqJktsLsxw1gTF2002KI&_rdr"
            >
              <x-image
                mode="width"
                ${css({ width: '27px' })}
                .value=${require('client/assets/social_media_icons/facebook.svg')}
              ></x-image>
            </x-link>
            <x-link href="https://www.linkedin.com/company/welcometogermany/">
              <x-image
                mode="width"
                ${css({ width: '27px' })}
                .value=${require('client/assets/social_media_icons/linkedin.svg')}
              ></x-image>
            </x-link>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private validateEmail(email: string) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }
}
